import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Plus, Trash2, Upload, FileDown, Sparkles, History, ShieldCheck, Loader2 } from "lucide-react";

const EMPTY = {
  contact: { full_name: "", email: "", phone: "", location: "", linkedin: "", website: "" },
  personal_statement: "",
  experience: [],
  education: [],
  skills: [],
  certifications: [],
  languages: [],
  volunteering: [],
  references: "References available on request.",
};

const listToText = (a) => (a || []).join("\n");
const textToList = (t) => t.split("\n").map((s) => s.trim()).filter(Boolean);

function Field({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <label className="block text-sm">
      <span className="text-slate-600">{label}</span>
      <input data-testid={testid} type={type} className="field mt-1" value={value || ""}
        placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function Preview({ cv }) {
  const s = cv.sections || EMPTY;
  const c = s.contact || {};
  const meta = [c.location, c.phone, c.email, c.linkedin, c.website].filter(Boolean).join(" · ");
  const Block = ({ title, children }) => (
    <div className="mt-4">
      <h4 className="text-[11px] font-bold uppercase tracking-wider text-blue-900 border-b border-slate-200 pb-1">{title}</h4>
      {children}
    </div>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 text-[12px] leading-relaxed shadow-sm" data-testid="cv-preview">
      <p className="text-xl font-bold text-slate-900">{c.full_name || "Your name"}</p>
      {meta && <p className="text-[11px] text-slate-500 mt-1">{meta}</p>}
      {s.personal_statement && <Block title="Personal statement"><p className="mt-1 text-slate-700">{s.personal_statement}</p></Block>}
      {(s.experience || []).length > 0 && (
        <Block title="Work experience">
          {s.experience.map((e, i) => (
            <div key={i} className="mt-2">
              <p className="font-semibold text-slate-900">{[e.job_title, e.employer].filter(Boolean).join(" — ")}</p>
              <p className="text-[11px] text-slate-500">{[e.location, [e.start_date, e.is_current ? "Present" : e.end_date].filter(Boolean).join(" – ")].filter(Boolean).join(" · ")}</p>
              <ul className="list-disc ml-4 mt-1 text-slate-700">{(e.bullets || []).map((b, j) => <li key={j}>{b}</li>)}</ul>
            </div>
          ))}
        </Block>
      )}
      {(s.education || []).length > 0 && (
        <Block title="Education and qualifications">
          {s.education.map((e, i) => (
            <div key={i} className="mt-2">
              <p className="font-semibold text-slate-900">{e.qualification}</p>
              <p className="text-[11px] text-slate-500">{[e.institution, [e.start_date, e.end_date].filter(Boolean).join(" – "), e.grade].filter(Boolean).join(" · ")}</p>
            </div>
          ))}
        </Block>
      )}
      {[["skills", "Skills"], ["certifications", "Certifications and training"], ["languages", "Languages"], ["volunteering", "Volunteering"]].map(([k, t]) =>
        (s[k] || []).length > 0 ? <Block key={k} title={t}><ul className="list-disc ml-4 mt-1 text-slate-700">{s[k].map((v, i) => <li key={i}>{v}</li>)}</ul></Block> : null
      )}
      {s.references && <Block title="References"><p className="mt-1 text-slate-700">{s.references}</p></Block>}
    </div>
  );
}

export default function CvBuilder() {
  const [cvs, setCvs] = useState([]);
  const [cv, setCv] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [versions, setVersions] = useState([]);
  const [letter, setLetter] = useState(null);
  const [letterForm, setLetterForm] = useState({ company_name: "", role_title: "", hiring_manager: "Hiring Manager", why_this_employer: "" });
  const [importInfo, setImportInfo] = useState(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get("returnTo") || "";
  const originatingJobId = returnTo.match(/^\/jobs\/([^/?]+)/)?.[1] || "";

  const loadList = async () => {
    const { data } = await api.get("/cvs");
    setCvs(data);
    return data;
  };

  useEffect(() => {
    (async () => {
      try {
        const [t, list] = await Promise.all([api.get("/cvs/templates"), api.get("/cvs")]);
        setTemplates(t.data);
        setCvs(list.data);
        if (list.data.length) setCv(list.data[0]);
      } catch (e) {
        setMsg("Please sign in to use the CV builder.");
      }
    })();
  }, []);

  const create = async (template) => {
    const { data } = await api.post("/cvs", { name: `CV — ${template.replace(/_/g, " ")}`, template, sections: EMPTY });
    await loadList();
    setCv(data);
    setAnalysis(null);
    setMsg("New CV created. Complete each section, then export.");
  };

  const setSection = (key, value) => setCv({ ...cv, sections: { ...cv.sections, [key]: value } });
  const setContact = (k, v) => setSection("contact", { ...(cv.sections.contact || {}), [k]: v });

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/cvs/${cv.id}`, {
        name: cv.name, template: cv.template, target_role: cv.target_role || "", sections: cv.sections,
      });
      setCv(data);
      await loadList();
      setMsg(`Saved as version ${data.version}.`);
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not save this CV."); }
    setBusy("");
  };

  const submitForJob = async () => {
    if (!originatingJobId) {
      navigate("/jobs");
      return;
    }
    setBusy("submit");
    try {
      await save();
      await api.post("/applications", { job_id: originatingJobId, cv_id: cv.id, answers: {}, cover_letter: "" });
      setMsg("Your selected CV has been submitted for this job. Track it from your dashboard.");
      navigate(`/jobs/${originatingJobId}`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not submit this CV for the job.");
    }
    setBusy("");
  };

  const analyse = async () => {
    setBusy("analyse");
    const { data } = await api.post(`/cvs/${cv.id}/ats-analysis`);
    setAnalysis(data);
    setBusy("");
  };

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get(`/cvs/${cv.id}/export`, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(cv.sections?.contact?.full_name || "cv").replace(/\s+/g, "-")}-CV.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg(`${format.toUpperCase()} downloaded.`);
    } catch (e) { setMsg("Export failed. Please save the CV and try again."); }
    setBusy("");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("import");
    setImportInfo(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cvs/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadList();
      setCv(data);
      setImportInfo(data);
      setMsg(`Imported. Extraction confidence ${Math.round(data.extraction_confidence * 100)}%. Please review every section.`);
    } catch (err) { setMsg(err?.response?.data?.detail || "Import failed."); }
    setBusy("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const confirmExtraction = async () => {
    await api.post(`/cvs/${cv.id}/confirm-extraction`);
    setImportInfo(null);
    setMsg("You have confirmed the extracted content is accurate.");
  };

  const loadVersions = async () => setVersions((await api.get(`/cvs/${cv.id}/versions`)).data);
  const restore = async (vid) => {
    const { data } = await api.post(`/cvs/${cv.id}/versions/${vid}/restore`);
    setCv(data);
    setVersions([]);
    setMsg("Earlier version restored as a new version.");
  };

  const draftLetter = async () => {
    setBusy("letter");
    try {
      const { data } = await api.post(`/cvs/${cv.id}/cover-letter`, letterForm);
      setLetter(data);
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not draft a cover letter."); }
    setBusy("");
  };

  const requestReview = async () => {
    try {
      const { data } = await api.post(`/cvs/${cv.id}/request-human-review`);
      setMsg(data.note);
      setCv({ ...cv, human_review_status: "requested" });
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not request a review."); }
  };

  const addExp = () => setSection("experience", [...(cv.sections.experience || []), { job_title: "", employer: "", location: "", start_date: "", end_date: "", is_current: false, bullets: [] }]);
  const updExp = (i, k, v) => setSection("experience", cv.sections.experience.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  const delExp = (i) => setSection("experience", cv.sections.experience.filter((_, j) => j !== i));
  const addEdu = () => setSection("education", [...(cv.sections.education || []), { qualification: "", institution: "", location: "", start_date: "", end_date: "", grade: "" }]);
  const updEdu = (i, k, v) => setSection("education", cv.sections.education.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  const delEdu = (i) => setSection("education", cv.sections.education.filter((_, j) => j !== i));

  return (
    <div className="max-w-7xl mx-auto px-5 py-10" data-testid="cv-builder-page">
      <p className="overline">CV and cover-letter builder</p>
      <h1 className="mt-3 text-3xl font-semibold">Build an ATS-friendly CV</h1>
      <p className="mt-3 text-sm text-slate-600 max-w-3xl">
        EuropEdu never invents employment, skills, qualifications or achievements. Imported files are
        read and structured exactly as written; every suggestion requires your confirmation.
      </p>
      {msg && <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="cv-message">{msg}</p>}

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {templates.map((t) => (
          <button key={t.key} data-testid={`create-cv-${t.key}`} onClick={() => create(t.key)} className="btn btn-ghost" title={t.description}>
            <Plus className="w-4 h-4 mr-1" />{t.name}
          </button>
        ))}
        <span className="mx-2 h-5 w-px bg-slate-200" />
        <button data-testid="import-cv-button" onClick={() => fileRef.current?.click()} className="btn btn-primary">
          {busy === "import" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Import PDF or Word CV
        </button>
        <input ref={fileRef} data-testid="cv-file-input" type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={upload} />
      </div>

      {cvs.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="cv-list">
          {cvs.map((c) => (
            <button key={c.id} data-testid={`select-cv-${c.id}`} onClick={() => { setCv(c); setAnalysis(null); setVersions([]); setLetter(null); }}
              className={`pill border ${cv?.id === c.id ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-700 border-slate-200"}`}>
              {c.name} · v{c.version}
            </button>
          ))}
        </div>
      )}

      {importInfo && (
        <section className="card p-5 mt-6 border-amber-200 bg-amber-50" data-testid="import-review-panel">
          <p className="text-sm font-semibold">Review the extracted content</p>
          <p className="text-xs text-slate-700 mt-1">
            Detected sections: {(importInfo.detected_headings || []).join(", ") || "none"} · confidence {Math.round(importInfo.extraction_confidence * 100)}%.
            {" "}{importInfo.note}
          </p>
          <button data-testid="confirm-extraction-button" onClick={confirmExtraction} className="btn btn-primary mt-3">I have checked the extracted content</button>
        </section>
      )}

      {cv && (
        <div className="mt-8 grid lg:grid-cols-[1.15fr_1fr] gap-8 items-start">
          <div className="space-y-6">
            <section className="card p-6">
              <h2 className="font-semibold">CV details</h2>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <Field label="CV name" testid="cv-name-input" value={cv.name} onChange={(v) => setCv({ ...cv, name: v })} />
                <label className="block text-sm">
                  <span className="text-slate-600">Template</span>
                  <select data-testid="cv-template-select" className="field mt-1" value={cv.template} onChange={(e) => setCv({ ...cv, template: e.target.value })}>
                    {templates.map((t) => <option key={t.key} value={t.key}>{t.name}{t.ats_safe ? " (ATS safe)" : ""}</option>)}
                  </select>
                </label>
                <Field label="Target role" testid="cv-target-role-input" value={cv.target_role} onChange={(v) => setCv({ ...cv, target_role: v })} placeholder="e.g. Registered Nurse" />
              </div>
            </section>

            <section className="card p-6">
              <h2 className="font-semibold">Contact details</h2>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <Field label="Full name" testid="contact-name-input" value={cv.sections?.contact?.full_name} onChange={(v) => setContact("full_name", v)} />
                <Field label="Email" testid="contact-email-input" type="email" value={cv.sections?.contact?.email} onChange={(v) => setContact("email", v)} />
                <Field label="Telephone" testid="contact-phone-input" value={cv.sections?.contact?.phone} onChange={(v) => setContact("phone", v)} />
                <Field label="Location" testid="contact-location-input" value={cv.sections?.contact?.location} onChange={(v) => setContact("location", v)} />
                <Field label="LinkedIn" testid="contact-linkedin-input" value={cv.sections?.contact?.linkedin} onChange={(v) => setContact("linkedin", v)} />
                <Field label="Website" testid="contact-website-input" value={cv.sections?.contact?.website} onChange={(v) => setContact("website", v)} />
              </div>
              <p className="mt-3 text-xs text-slate-500">UK CVs should not include a photograph, date of birth, marital status or nationality.</p>
            </section>

            <section className="card p-6">
              <h2 className="font-semibold">Personal statement</h2>
              <textarea data-testid="personal-statement-input" rows={5} className="field mt-3" value={cv.sections?.personal_statement || ""}
                onChange={(e) => setSection("personal_statement", e.target.value)}
                placeholder="Describe your actual experience, sector and what you are looking for." />
            </section>

            <section className="card p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold">Work experience</h2>
                <button data-testid="add-experience-button" onClick={addExp} className="btn btn-ghost py-1 px-3"><Plus className="w-4 h-4 mr-1" />Add role</button>
              </div>
              {(cv.sections?.experience || []).map((e, i) => (
                <div key={i} className="mt-4 border border-slate-200 rounded-lg p-4" data-testid={`experience-item-${i}`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Job title" testid={`exp-title-${i}`} value={e.job_title} onChange={(v) => updExp(i, "job_title", v)} />
                    <Field label="Employer" testid={`exp-employer-${i}`} value={e.employer} onChange={(v) => updExp(i, "employer", v)} />
                    <Field label="Location" testid={`exp-location-${i}`} value={e.location} onChange={(v) => updExp(i, "location", v)} />
                    <Field label="Start (e.g. Mar 2021)" testid={`exp-start-${i}`} value={e.start_date} onChange={(v) => updExp(i, "start_date", v)} />
                    <Field label="End" testid={`exp-end-${i}`} value={e.end_date} onChange={(v) => updExp(i, "end_date", v)} />
                    <label className="flex items-center gap-2 text-sm mt-6">
                      <input data-testid={`exp-current-${i}`} type="checkbox" checked={!!e.is_current} onChange={(ev) => updExp(i, "is_current", ev.target.checked)} />
                      This is my current role
                    </label>
                  </div>
                  <label className="block text-sm mt-3">
                    <span className="text-slate-600">Achievements — one per line</span>
                    <textarea data-testid={`exp-bullets-${i}`} rows={4} className="field mt-1" value={listToText(e.bullets)}
                      onChange={(ev) => updExp(i, "bullets", textToList(ev.target.value))} />
                  </label>
                  <button data-testid={`remove-experience-${i}`} onClick={() => delExp(i)} className="btn btn-ghost mt-2 py-1 px-3 text-rose-700"><Trash2 className="w-4 h-4 mr-1" />Remove</button>
                </div>
              ))}
            </section>

            <section className="card p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold">Education and qualifications</h2>
                <button data-testid="add-education-button" onClick={addEdu} className="btn btn-ghost py-1 px-3"><Plus className="w-4 h-4 mr-1" />Add qualification</button>
              </div>
              {(cv.sections?.education || []).map((e, i) => (
                <div key={i} className="mt-4 border border-slate-200 rounded-lg p-4" data-testid={`education-item-${i}`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Qualification" testid={`edu-qualification-${i}`} value={e.qualification} onChange={(v) => updEdu(i, "qualification", v)} />
                    <Field label="Institution" testid={`edu-institution-${i}`} value={e.institution} onChange={(v) => updEdu(i, "institution", v)} />
                    <Field label="Start" testid={`edu-start-${i}`} value={e.start_date} onChange={(v) => updEdu(i, "start_date", v)} />
                    <Field label="End" testid={`edu-end-${i}`} value={e.end_date} onChange={(v) => updEdu(i, "end_date", v)} />
                    <Field label="Grade" testid={`edu-grade-${i}`} value={e.grade} onChange={(v) => updEdu(i, "grade", v)} />
                  </div>
                  <button data-testid={`remove-education-${i}`} onClick={() => delEdu(i)} className="btn btn-ghost mt-2 py-1 px-3 text-rose-700"><Trash2 className="w-4 h-4 mr-1" />Remove</button>
                </div>
              ))}
            </section>

            <section className="card p-6">
              <h2 className="font-semibold">Skills, certifications and languages</h2>
              {[["skills", "Skills — one per line"], ["certifications", "Certifications and training"], ["languages", "Languages"], ["volunteering", "Volunteering"]].map(([k, label]) => (
                <label key={k} className="block text-sm mt-3">
                  <span className="text-slate-600">{label}</span>
                  <textarea data-testid={`${k}-input`} rows={3} className="field mt-1" value={listToText(cv.sections?.[k])}
                    onChange={(e) => setSection(k, textToList(e.target.value))} />
                </label>
              ))}
              <label className="block text-sm mt-3">
                <span className="text-slate-600">References</span>
                <textarea data-testid="references-input" rows={2} className="field mt-1" value={cv.sections?.references || ""}
                  onChange={(e) => setSection("references", e.target.value)} />
              </label>
            </section>

            <section className="card p-6">
              <h2 className="font-semibold">Cover letter</h2>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <Field label="Company" testid="letter-company-input" value={letterForm.company_name} onChange={(v) => setLetterForm({ ...letterForm, company_name: v })} />
                <Field label="Role title" testid="letter-role-input" value={letterForm.role_title} onChange={(v) => setLetterForm({ ...letterForm, role_title: v })} />
                <Field label="Addressed to" testid="letter-manager-input" value={letterForm.hiring_manager} onChange={(v) => setLetterForm({ ...letterForm, hiring_manager: v })} />
              </div>
              <label className="block text-sm mt-3">
                <span className="text-slate-600">Why this employer (your own words)</span>
                <textarea data-testid="letter-why-input" rows={3} className="field mt-1" value={letterForm.why_this_employer}
                  onChange={(e) => setLetterForm({ ...letterForm, why_this_employer: e.target.value })} />
              </label>
              <button data-testid="draft-cover-letter-button" onClick={draftLetter} className="btn btn-primary mt-3">
                {busy === "letter" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}Draft cover letter
              </button>
              {letter && (
                <div className="mt-4" data-testid="cover-letter-output">
                  <pre className="whitespace-pre-wrap text-sm bg-slate-50 border border-slate-200 rounded p-4">{letter.body}</pre>
                  <p className="mt-2 text-xs text-slate-500">{letter.note}</p>
                </div>
              )}
            </section>
          </div>

          <div className="space-y-6 lg:sticky lg:top-6">
            <div className="card p-5">
              <div className="flex flex-wrap gap-2">
                <button data-testid="save-cv-button" onClick={save} className="btn btn-primary">
                  {busy === "save" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Save version
                </button>
                <button data-testid="submit-cv-button" onClick={submitForJob} className="btn btn-ghost" disabled={busy === "submit"}>
                  {busy === "submit" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}{originatingJobId ? "Submit this CV for the job" : "Find a job for this CV"}
                </button>
                <button data-testid="export-pdf-button" onClick={() => download("pdf")} className="btn btn-ghost"><FileDown className="w-4 h-4 mr-1" />PDF</button>
                <button data-testid="export-docx-button" onClick={() => download("docx")} className="btn btn-ghost"><FileDown className="w-4 h-4 mr-1" />Word</button>
                <button data-testid="ats-analysis-button" onClick={analyse} className="btn btn-ghost"><ShieldCheck className="w-4 h-4 mr-1" />ATS analysis</button>
                <button data-testid="versions-button" onClick={loadVersions} className="btn btn-ghost"><History className="w-4 h-4 mr-1" />Versions</button>
                <button data-testid="request-review-button" onClick={requestReview} className="btn btn-ghost">Request human review</button>
              </div>
              <p className="mt-3 text-xs text-slate-500">Exports are generated on the server from the version you have saved.</p>
            </div>

            {analysis && (
              <section className="card p-5" data-testid="ats-analysis">
                <div className="flex items-baseline justify-between">
                  <h2 className="font-semibold">ATS readiness</h2>
                  <span data-testid="ats-score" className="text-2xl font-bold text-blue-900">{analysis.score}/100</span>
                </div>
                <ul className="mt-3 text-sm grid sm:grid-cols-2 gap-1">
                  {Object.entries(analysis.checks).map(([k, v]) => (
                    <li key={k} className={v ? "text-emerald-700" : "text-amber-800"}>
                      {v ? "✓" : "!"} {k.replace(/_/g, " ")}
                    </li>
                  ))}
                </ul>
                {(analysis.issues || []).length > 0 && (
                  <ul className="mt-3 list-disc ml-4 text-sm text-slate-700 space-y-1" data-testid="ats-issues">
                    {analysis.issues.map((i, n) => <li key={n}>{i}</li>)}
                  </ul>
                )}
                <p className="mt-3 text-xs text-slate-500">{analysis.note}</p>
              </section>
            )}

            {versions.length > 0 && (
              <section className="card p-5" data-testid="cv-versions">
                <h2 className="font-semibold">Earlier versions</h2>
                <ul className="mt-3 text-sm space-y-2">
                  {versions.map((v) => (
                    <li key={v.id} className="flex items-center justify-between">
                      <span>Version {v.archived_version} · {new Date(v.archived_at).toLocaleString("en-GB")}</span>
                      <button data-testid={`restore-version-${v.id}`} onClick={() => restore(v.id)} className="btn btn-ghost py-1 px-3">Restore</button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <Preview cv={cv} />
          </div>
        </div>
      )}
    </div>
  );
}
